import { useState } from "react";
import { IconButton, Tooltip } from "@material-ui/core";
import micFill from "@iconify/icons-eva/mic-fill";
import stopCircleFill from "@iconify/icons-eva/stop-circle-fill";
import Iconify from "src/components/iconify";
import PropTypes from "prop-types";
import { useSnackbar } from "src/components/snackbar";

const SpeechRecognitionAPI =
  window.SpeechRecognition || window.webkitSpeechRecognition || null;

/**
 * Botón de dictado por voz usando Web Speech API (solo Chromium).
 * Retorna null si el navegador no soporta la API (Firefox, etc.).
 *
 * Props:
 *   onTranscript — callback(texto) con el texto reconocido
 *   disabled     — deshabilita el botón
 */
const VoiceInputButton = ({ onTranscript, disabled }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [escuchando, setEscuchando] = useState(false);

  // No renderizar en navegadores sin soporte
  if (!SpeechRecognitionAPI) return null;

  const handleClick = () => {
    if (escuchando) {
      // El estado se limpia en onend — solo necesitamos señalizar la detención
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.lang = "es-CL";
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => setEscuchando(true);

    recognition.onresult = (event) => {
      const texto = event.results[0][0].transcript;
      onTranscript(texto);
    };

    recognition.onerror = (event) => {
      setEscuchando(false);
      if (event.error === "not-allowed") {
        enqueueSnackbar(
          "Permiso de micrófono denegado. Actívalo en la configuración del navegador.",
          { variant: "error" },
        );
      } else if (event.error !== "no-speech") {
        // "no-speech" es silencio normal — no mostrar error
        enqueueSnackbar("No se pudo reconocer el audio. Intenta nuevamente.", {
          variant: "warning",
        });
      }
    };

    recognition.onend = () => setEscuchando(false);

    recognition.start();
  };

  return (
    <Tooltip
      title={escuchando ? "Escuchando... (habla ahora)" : "Dictar con voz"}
    >
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          onClick={handleClick}
          style={{ color: escuchando ? "#d32f2f" : undefined }}
        >
          <Iconify
            icon={escuchando ? stopCircleFill : micFill}
            width={20}
            height={20}
          />
        </IconButton>
      </span>
    </Tooltip>
  );
};

VoiceInputButton.propTypes = {
  onTranscript: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

VoiceInputButton.defaultProps = {
  disabled: false,
};

export default VoiceInputButton;
